// src/components/dashboard/PersonalizedRoadmap.jsx (完成版)

import React, { useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import { useUserProfile } from '../../contexts/nobishiro-dashboard/useUserProfile';
import { generatePersonalizedRoadmap } from '../../services/aiAnalysisService';
import { Loader, Target, Zap, Brain, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

// 個別のステップを表示するためのサブコンポーネント
const RoadmapStep = ({ step, index }) => (
  <div className="p-6 rounded-2xl bg-white border border-gray-200 shadow-sm transition-shadow hover:shadow-lg">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center font-bold text-2xl text-blue-600 border-4 border-white ring-2 ring-blue-200">
        {index + 1}
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-gray-900">{step.title}</h3>
        <p className="text-sm text-gray-600 mt-1">{step.description}</p>
        <div className="mt-4 pt-4 border-t border-dashed">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">具体的なアクション</h4>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            {step.actions.map((action, i) => (
              <li key={i}>{action}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const PersonalizedRoadmap = () => {
  const { user } = useAuth();
  // useUserProfileフックを使用して、ユーザープロファイルを読み込みます
  const { profile, loading } = useUserProfile(user?.uid);

  // 進行中の目標を抽出します
  const activeGoal = useMemo(() => {
    if (!profile?.talentPortfolio?.growthGoals) return null;
    // 「進行中」の目標を優先し、なければ「未着手」の目標を探します
    return profile.talentPortfolio.growthGoals.find(g => g.status === 'in-progress') || 
           profile.talentPortfolio.growthGoals.find(g => g.status === 'todo');
  }, [profile]);
  
  // AIによるロードマップ生成を実行します
  const roadmap = useMemo(() => {
    if (!activeGoal || !profile) return null;
    return generatePersonalizedRoadmap(activeGoal, profile);
  }, [activeGoal, profile]);

  // データ読み込み中の表示
  if (loading) {
    return (
      <div className="flex justify-center items-center p-20">
        <Loader className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  // 目標が設定されていない場合の表示
  if (!activeGoal) {
    return (
      <div className="text-center p-8 bg-white rounded-lg shadow-md">
        <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800">目標が設定されていません</h2>
        <p className="text-gray-600 mt-2 mb-6">パーソナライズされた学習ロードマップを生成するには、まず目標を設定する必要があります。</p>
        <Link to="/goal-setting" className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-transform hover:scale-105 inline-block">
          目標設定ページへ
        </Link>
      </div>
    );
  }

  // 正常時の表示
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <div className="inline-block p-4 bg-yellow-100 rounded-full mb-4">
          <Zap className="w-10 h-10 text-yellow-500" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 tracking-tight">パーソナル・ロードマップ</h1>
        <p className="text-lg text-gray-600 mt-2">AIがあなたの目標達成への最短経路をナビゲートします。</p>
      </div>

      <div className="p-6 rounded-xl bg-indigo-50 border-l-4 border-indigo-500 shadow-sm">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-indigo-600" />
          現在のターゲット目標
        </h2>
        <p className="text-xl font-bold text-indigo-800 mb-4">「{activeGoal.text}」</p>
        
        <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
          <Brain className="w-5 h-5 text-indigo-600" />
          AIによるギャップ分析サマリー
        </h3>
        <p className="text-sm text-indigo-900 leading-relaxed">
          {roadmap?.summary || '分析中です...'}
        </p>
      </div>

      <div className="space-y-6">
        {roadmap?.steps.map((step, index) => <RoadmapStep key={step.priority} step={step} index={index} />)}
      </div>
    </div>
  );
};

export default PersonalizedRoadmap;